import { useDate } from '@/composables/useDate.ts'
import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { useTimeslotHelpers } from '@/composables/useTimeslotHelpers.ts'
import {
  DEVICE_MODE_CHANGED_TO_MEASURE,
  DEVICE_STATUS_NOT_REACHABLE,
  DEVICE_STATUS_SWITCHED_OFF,
  DEVICE_STATUS_SWITCHED_ON,
  DEVICE_TYPE_MYSTROM_WIFI_SWITCH,
  SENSOR_DATA_IS_ON,
  SENSOR_DATA_POWER,
  SENSOR_DATA_TEMPERATURE,
} from '@/constants'
import { useCubeStore } from '@/stores/useCubeStore'
import { useFormsStore } from '@/stores/useFormsStore'
import { defineStore } from 'pinia'
import { io } from 'socket.io-client'
import { computed, ref } from 'vue'

const { parseTimeSlots, stringifyTimeSlots } = useTimeslotHelpers()
const { moment } = useDate()

export const useDeviceStore = defineStore('device', () => {
  const { logError } = useLogError()
  const { deviceManagerApi } = useHttpClient()

  // --- State ---

  const devices = ref<Record<string, any>>({})
  const devicesLoaded = ref(false)
  // --- Computed ---

  const devicesList = computed(() => Object.values(devices.value))

  const myStromWifiSwitchDevices = computed(() =>
    devicesList.value.filter((d) => d.deviceType === DEVICE_TYPE_MYSTROM_WIFI_SWITCH)
  )

  // --- Helpers ---

  function preprocessDevice(rawDevice: any) {
    const monday = rawDevice['manual_time_slots_mon_']
    const tuesday = rawDevice['manual_time_slots_tue_']
    const wednesday = rawDevice['manual_time_slots_wed_']
    const thursday = rawDevice['manual_time_slots_thu_']
    const friday = rawDevice['manual_time_slots_fri_']
    const saturday = rawDevice['manual_time_slots_sat_']
    const sunday = rawDevice['manual_time_slots_sun_']

    const setForAllDays =
      monday === tuesday &&
      tuesday === wednesday &&
      wednesday === thursday &&
      thursday === friday &&
      friday === saturday &&
      saturday === sunday &&
      sunday === monday

    const active = rawDevice.mode === 'manual'
    return {
      ...rawDevice,
      timer: {
        active,
        isValid: true,
        setForAllDays,
        defaultOnState: rawDevice.default_state === 'true',
        all: parseTimeSlots(monday),
        monday: parseTimeSlots(monday),
        tuesday: parseTimeSlots(tuesday),
        wednesday: parseTimeSlots(wednesday),
        thursday: parseTimeSlots(thursday),
        friday: parseTimeSlots(friday),
        saturday: parseTimeSlots(saturday),
        sunday: parseTimeSlots(sunday),
      },
      isReachable: 'unknown',
      reads: {},
      consumption: [],
    }
  }

  // --- Actions ---

  async function scanForNewDevices(deviceType: string) {
    try {
      const formsStore = useFormsStore()
      const cubeStore = useCubeStore()
      formsStore.setFormState('scanForNewDevices', 'submitting')

      const url = `https://${cubeStore.deviceManagerSocketHost}/api/app/v1.0/device-network-manager/scan`
      const result = await deviceManagerApi.get(url, {
        params: { onlyNew: true, deviceType },
      })
      formsStore.setFormState('scanForNewDevices', 'finished')

      if (!result || !result.data || result.data.error) {
        throw new Error(`Requesting devices returned error: ${result.data.error}`)
      }

      for (const rawDevice of result.data.data.saveResult) {
        const deviceData = preprocessDevice(rawDevice)
        devices.value[deviceData.id] = deviceData
      }

      await getAllDeviceStates()
    } catch (err: any) {
      if (err.response?.status === 404) {
        const formsStore = useFormsStore()
        formsStore.setFormState('scanForNewDevices', 'finished')
        logError('Scanning for new devices returned 404 - backend not available yet.')
      } else {
        logError(`Requesting cube devices failed: ${err}`)
      }
    }
  }

  async function getAllDevices() {
    try {
      const cubeStore = useCubeStore()

      const result = await deviceManagerApi.get('/devicesimple', {
        params: { cubeName: cubeStore.name },
      })

      if (!result || !result.data || result.data.error) {
        throw new Error(`Requesting devices returned error: ${JSON.stringify(result.data)}`)
      }

      for (const rawDevice of result.data.data) {
        const deviceData = preprocessDevice(rawDevice)
        devices.value[deviceData.id] = deviceData
      }
      devicesLoaded.value = true

      const hasInverter = result.data.data.some(
        (dev: any) => dev.inverter_type !== undefined && dev.inverter_type !== 'false'
      )
      const hasBattery = result.data.data.some((dev: any) => dev.battery === 'true')

      if (hasInverter) {
        cubeStore.showInverterData = true
      }
      if (hasBattery) {
        cubeStore.showBatteryData = true
      }

      setupDeviceManagerSocket()

      return await getAllDeviceStates()
    } catch (err: any) {
      logError(err)
    }

    return [];
  }

  async function getAllDeviceStates() {
    return Promise.all(devicesList.value.map((device) => getDeviceState(device)))
  }

  async function getDeviceState(device: any) {
    try {
      const cubeStore = useCubeStore()

      const url = `https://${cubeStore.deviceManagerSocketHost}/api/app/v1.0/device-control/report`
      const result = await deviceManagerApi.get(url, {
        params: { id: device.id },
      })

      if (!result || !result.data || !result.data.data || result.data.error) {
        throw new Error(`Error getting device state: ${result.data.data}`)
      }

      devices.value[device.id] = {
        reads: {},
        ...devices.value[device.id],
        isOn: !!result.data.data.switchIsOn,
        isOnChangedLocally: false,
        wattage: result.data.data.power,
        temperature: result.data.data.temperature,
        isReachable: true,
      }
    } catch (err: any) {
      if (err.response?.status === 422 && err.response?.data?.data === 'Device not available') {
        devices.value[device.id] = {
          ...devices.value[device.id],
          reads: {},
          consumption: [],
          isReachable: false,
        }
      } else {
        logError(err)
      }
    }
  }

  async function setDeviceMode(data: { deviceId: string; mode: string }) {
    try {
      const formsStore = useFormsStore()
      formsStore.setFormState('deviceMode', 'submitting')

      const result = await deviceManagerApi.put(`/device/${data.deviceId}`, {}, { params: { mode: data.mode } })

      if (!result || !result.data || !result.data.data || result.data.error) {
        throw new Error(`Error setting device mode: ${result.data.error.Message}`)
      }

      formsStore.setFormState('deviceMode', 'finished')
      devices.value[data.deviceId].mode = data.mode
    } catch (err: any) {
      logError(err)
    }
  }

  async function toggleDeviceOn(device: any) {
    try {
      const formsStore = useFormsStore()
      const cubeStore = useCubeStore()
      formsStore.setFormState('deviceOn_' + device.id, 'submitting')

      // Animate the power icon to show we're waiting for a request to finish.
      devices.value[device.id].isOnChangedLocally = true

      const url = `https://${cubeStore.deviceManagerSocketHost}/api/app/v1.0/device-control/control`
      const result = await deviceManagerApi.get(url, {
        params: {
          deviceId: device.id,
          type: device.deviceType,
          address: device.ip_address,
          // We need to toggle its state, so invert
          state: !device.isOn ? 'on' : 'off',
        },
      })

      formsStore.setFormState('deviceOn_' + device.id, 'finished')

      if (!result || !result.data || result.data.error) {
        throw new Error(`Error setting device state: ${JSON.stringify(result.data)}`)
      }

      // Note that we do not mutate the device's isOn state here because the request
      // we just made can take several seconds to return and in the meantime a
      // sensor data socket event can change isOn back to its previous state until
      // the next event changes it to its correct value.
      // To mitigate this problem we skip setting isOn here and wait until a sensor
      // data socket event tells us to mark the device as being switched on.
      // Instead we set isOnChangedLocally to animate the power icon as pending.
    } catch (err: any) {
      logError(err)
    }
  }

  async function updateDevice(data: {
    deviceId: string
    name: string
    description: string
    applianceType: string
    deviceType?: string
  }) {
    try {
      const formsStore = useFormsStore()
      formsStore.setFormState('deviceSettings', 'submitting')

      const params = {
        id: data.deviceId,
        name: data.name,
        description: data.description,
        appliance_type: data.applianceType,
      }
      const result = await deviceManagerApi.put('/devicesimple', params)

      formsStore.setFormState('deviceSettings', 'finished')

      if (!result || !result.data || result.data.error) {
        throw new Error(`Error updating device: ${JSON.stringify(result.data)}`)
      }

      devices.value[data.deviceId] = {
        ...devices.value[data.deviceId],
        name: data.name,
        description: data.description,
        appliance_type: data.applianceType,
        ...(data.deviceType ? { deviceType: data.deviceType } : {}),
      }
    } catch (err: any) {
      logError(err)
    }
  }

  async function getDeviceReads(data: { deviceId: string; date: string; timespan: string }) {
    try {
      let granularity = '1m'
      let labelsDateFormat = 'HH:mm'
      if (data.timespan !== 'day') {
        granularity = '1d'
        labelsDateFormat = 'E dd.MM.'
      }

      const today = moment(data.date, 'YYYY-MM-DD')
      const params = {
        deviceId: data.deviceId,
        readingTypes: 'Consumption W',
        labelsDateFormat,
        granularity,
        startDate: today.startOf(data.timespan as any).toISOString(),
        endDate: today.endOf(data.timespan as any).toISOString(),
      }

      const result = await deviceManagerApi.get('/app/device-reads', { params })

      if (!result || !result.data || result.data.error) {
        throw new Error(`Error fetching device reads: ${JSON.stringify(result.data)}`)
      }

      const deviceId = parseInt(data.deviceId)
      let startDate = moment(result.data.data.start_date)
      const reads = {
        labels:
          data.timespan === 'day'
            ? result.data.data.labels.map(() => {
                const s = startDate.toISOString()
                startDate.add(1, 'minutes')
                return s
              })
            : result.data.data.labels.map(() => {
                const s = startDate.toISOString()
                startDate.add(1, 'days')
                return s
              }),
        datasets: result.data.data.reads.map((d: any) => {
          const consumption = d.reading_types['Consumption W']
          return {
            data:
              data.timespan === 'day'
                ? consumption.values
                : consumption.calculated_wh.values,
            sum: consumption.calculated_wh.sum,
            label: d.device_name,
            type: d.appliance_type.toLowerCase(),
          }
        }),
      }

      const sums = result.data.data.reads
        .map((d: any) => ({
          value: d.reading_types['Consumption W'].calculated_wh.sum,
          start: new Date(d.original_start_time),
          end: d.original_end_time ? new Date(d.original_end_time) : false,
          label: d.device_name,
          type: d.appliance_type.toLowerCase(),
        }))
        .sort((a: any, b: any) => b.start - a.start)

      devices.value[deviceId] = {
        ...devices.value[deviceId],
        reads,
        consumption: sums,
      }
    } catch (err: any) {
      logError(err)
    }
  }

  async function saveDeviceTimer(device: any) {
    const formsStore = useFormsStore()
    formsStore.setFormState('deviceTimer', 'submitting')

    const timerParams: Record<string, string> = {
      manual_time_slots_mon_: '',
      manual_time_slots_tue_: '',
      manual_time_slots_wed_: '',
      manual_time_slots_thu_: '',
      manual_time_slots_fri_: '',
      manual_time_slots_sat_: '',
      manual_time_slots_sun_: '',
    }

    if (device.timer.setForAllDays) {
      const values = stringifyTimeSlots(device.timer.all)
      Object.keys(timerParams).forEach((key) => {
        timerParams[key] = values
      })
    } else {
      timerParams['manual_time_slots_mon_'] = stringifyTimeSlots(device.timer.monday)
      timerParams['manual_time_slots_tue_'] = stringifyTimeSlots(device.timer.tuesday)
      timerParams['manual_time_slots_wed_'] = stringifyTimeSlots(device.timer.wednesday)
      timerParams['manual_time_slots_thu_'] = stringifyTimeSlots(device.timer.thursday)
      timerParams['manual_time_slots_fri_'] = stringifyTimeSlots(device.timer.friday)
      timerParams['manual_time_slots_sat_'] = stringifyTimeSlots(device.timer.saturday)
      timerParams['manual_time_slots_sun_'] = stringifyTimeSlots(device.timer.sunday)
    }

    const params = {
      id: device.id,
      ...timerParams,
      mode: device.timer.active ? 'manual' : 'measure',
      default_state: device.timer.defaultOnState,
    }

    const result = await deviceManagerApi.put('/devicesimple', params)

    formsStore.setFormState('deviceTimer', 'finished')

    if (!result || !result.data || result.data.error) {
      throw new Error(`Error updating device timer: ${JSON.stringify(result.data)}`)
    }
  }

  async function deleteDevice(deviceId: string) {
    try {
      const formsStore = useFormsStore()
      formsStore.setFormState('deleteDevice', 'submitting')

      const result = await deviceManagerApi.delete(`/device/${deviceId}`)

      formsStore.setFormState('deleteDevice', 'finished')
      delete devices.value[deviceId]

      if (!result || !result.data || result.data.error) {
        throw new Error(`Error deleting device: ${JSON.stringify(result.data)}`)
      }
    } catch (err: any) {
      logError(err)
    }
  }

  function setupDeviceManagerSocket() {
    try {
      const cubeStore = useCubeStore()
      let socket: any = cubeStore.meterSocket

      if (cubeStore.hasSeparateMeterHardware) {
        const newSocket = io(cubeStore.deviceManagerSocketUrl as string, cubeStore.socketConfig as any)
        cubeStore.deviceManagerSocket = newSocket as any
        socket = newSocket
      }

      if (!socket) {
        return
      }

      socket.on('connect', () => {
        // connection established
      })
      socket.on('connect_error', () => {
        // connection lost
      })
      socket.on('SensorData', (rawData: any) => {
        deviceSensorDataReceived(rawData)
      })
      socket.on('DeviceStatusChange', (rawData: any) => {
        deviceStatusChangeReceived(rawData)
      })
    } catch (err) {
      logError(`Error setting up device manager socket connection: ${err}`)
    }
  }

  function deviceSensorDataReceived(data: any) {
    if (!Object.prototype.hasOwnProperty.call(devices.value, data.deviceId)) {
      logError(`Received device sensor data for unknown device with id ${data.deviceId}.`)
      return
    }
    for (const row of data.data) {
      switch (row.dataType) {
        case SENSOR_DATA_TEMPERATURE:
          devices.value[data.deviceId].temperature = row.value
          break
        case SENSOR_DATA_POWER:
          devices.value[data.deviceId].wattage = row.value
          break
        case SENSOR_DATA_IS_ON:
          devices.value[data.deviceId].isOn = row.value
          devices.value[data.deviceId].isOnChangedLocally = false
          break
      }
    }
    // If the device came back online automatically mark it that way.
    devices.value[data.deviceId].isReachable = true
  }

  function deviceStatusChangeReceived(data: any) {
    switch (parseInt(data.statusChangeId)) {
      case DEVICE_STATUS_SWITCHED_ON:
        devices.value[data.deviceId] = {
          ...devices.value[data.deviceId],
          isOn: true,
          isReachable: true,
          isOnChangedLocally: false,
        }
        break
      case DEVICE_STATUS_SWITCHED_OFF:
        devices.value[data.deviceId] = {
          ...devices.value[data.deviceId],
          isOn: false,
          wattage: 0,
          isReachable: true,
          isOnChangedLocally: false,
        }
        break
      case DEVICE_STATUS_NOT_REACHABLE:
        devices.value[data.deviceId].isReachable = false
        break
      case DEVICE_MODE_CHANGED_TO_MEASURE:
        devices.value[data.deviceId] = {
          ...devices.value[data.deviceId],
          mode: 'measure',
          timer: {
            ...devices.value[data.deviceId].timer,
            active: false,
          },
        }
        break
    }
  }

  function getDeviceById(deviceId: number | string) {
    return devices.value[deviceId]
  }

  return {
    // State
    devices,
    devicesLoaded,
    // Computed
    devicesList,
    myStromWifiSwitchDevices,
    // Actions
    getAllDevices,
    getAllDeviceStates,
    getDeviceState,
    setDeviceMode,
    toggleDeviceOn,
    updateDevice,
    getDeviceReads,
    saveDeviceTimer,
    deleteDevice,
    scanForNewDevices,
    setupDeviceManagerSocket,
    deviceSensorDataReceived,
    deviceStatusChangeReceived,
    getDeviceById,
  }
})
